import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { IoClose, IoGift, IoPlayCircle } from 'react-icons/io5';
import GiftBoxIcon from './GiftBoxIcon';
import POINT_SPOT_CONSTANTS from '../constants/PointSpotConstants';
// import PiLoginButton from './PiLoginButton'; // Removed unused import

// Animations
const popIn = keyframes`
  from { opacity: 0; transform: translateY(100%); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// 🎯 [UI 개선] SpotDetailModal 패턴 적용 - FloatingAddBtn(2000)보다 높은 z-index
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 2001; // FloatingAddBtn(2000)보다 높게 설정하여 모달이 위에 표시
  display: flex;
  justify-content: center;
  align-items: flex-end; // Bottom Sheet Style
  animation: ${fadeIn} 0.2s ease-out;
`;

// 🎯 [UI 개선] Safe-area 처리 및 하단 네비게이션 공간 확보
const SheetContent = styled.div`
  background: #1F2937; // Dark bg
  width: 100%;
  max-width: 500px;
  border-radius: 20px 20px 0 0;
  padding: 24px;
  padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 100px); // 🎯 하단 네비게이션 바(60px) + 여유(40px)
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  box-shadow: 0 -4px 20px rgba(0,0,0,0.5);
  animation: ${popIn} 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  border-top: 1px solid #374151;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  color: #9CA3AF;
  font-size: 24px;
  cursor: pointer;
  padding: 4px;
  z-index: 10;
`;

const Title = styled.h2`
  color: #F3F4F6;
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  text-align: center;
`;

const Subtitle = styled.p`
  color: #9CA3AF;
  font-size: 14px;
  margin: 0;
  text-align: center;
`;

const RewardCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #111827;
  border-radius: 16px;
  padding: 20px;
  width: 100%;
  border: 1px solid #374151;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 12px;
  margin-top: 8px;
`;

// Common Button Style
const ActionButton = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  border: none;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: transform 0.1s, opacity 0.2s;

  &:active {
    transform: scale(0.98);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const BasicButton = styled(ActionButton)`
  background: #374151;
  color: #E5E7EB;
`;

const AdButton = styled(ActionButton)`
  background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%);
  color: #FFFFFF;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: linear-gradient(rgba(255,255,255,0.2), transparent);
    opacity: 0;
    transition: opacity 0.2s;
  }
  &:hover::after {
    opacity: 1;
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255,255,255,0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const PointSpotRewardModal = ({
  isOpen,
  onClose,
  onClaimBasic,
  onClaimAd,
  rewardBasic,
  rewardAd,
  isLoading
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <SheetContent onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <IoClose />
        </CloseButton>

        <GiftBoxIcon size={80} />

        <Title>{t('giftbox_title')}</Title>
        <Subtitle>{t('giftbox_desc')}</Subtitle>

        <RewardCard>
          <ButtonGroup>
            {/* Option B: Ad Reward (Primary) */}
            <AdButton onClick={onClaimAd} disabled={isLoading}>
              {isLoading ? <LoadingSpinner /> : <IoPlayCircle size={20} />}
              <span>{t('giftbox_ad_btn', { amount: rewardAd || POINT_SPOT_CONSTANTS.DEFAULTS.REWARD_AD })}</span>
            </AdButton>

            {/* Option A: Basic Reward (Secondary) */}
            <BasicButton onClick={onClaimBasic} disabled={isLoading}>
              {isLoading ? <LoadingSpinner /> : <IoGift size={18} />}
              <span>{t('giftbox_basic_btn', { amount: rewardBasic || POINT_SPOT_CONSTANTS.DEFAULTS.REWARD_BASIC })}</span>
            </BasicButton>
          </ButtonGroup>
        </RewardCard>
      </SheetContent>
    </Overlay>
  );
};

export default PointSpotRewardModal;
